import React, { useCallback, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Download, FilePdf } from 'react-bootstrap-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import {
  EmailPreviewDto,
  GetAttachmentDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../translations';

interface EmailPreviewModalProps {
  show: boolean;
  onHide: () => void;
  preview: EmailPreviewDto;
  attachments?: GetAttachmentDto[];
}

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = props => {
  const { t, tk } = useTranslation('applicationLng');
  const pok = usePokCore();
  const attachments = props.attachments ?? [];
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>();

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentWindow?.document;
    if (!iframe || !doc) {
      return;
    }
    const borderHeight = iframe.offsetHeight - iframe.clientHeight;
    const buffer = 8;
    setIframeHeight(doc.documentElement.scrollHeight + borderHeight + buffer);
  }, []);
  return (
    <Modal
      show={props.show}
      onHide={props.onHide}
      centered
      size="xl"
      backdrop="static"
    >
      <Modal.Header closeButton className="border-0 bg-light align-items-start">
        <Modal.Title>{t(tk.email.preview)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="fs-5">{props.preview.subject}</div>
        </div>
        <iframe
          ref={iframeRef}
          className="email-preview-iframe d-block bg-white rounded border"
          srcDoc={props.preview.htmlContent}
          sandbox="allow-same-origin"
          onLoad={handleIframeLoad}
          style={iframeHeight ? { height: iframeHeight } : undefined}
        />
        {attachments.length > 0 && (
          <div className="mt-3">
            <div className="fw-bold mb-2">{t(tk.attachment.attachments)}</div>
            <div className="list-group">
              {attachments.map(attachment => (
                <div
                  key={attachment.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span className="text-truncate me-2">{attachment.name}</span>
                  <div className="d-flex gap-2 flex-shrink-0">
                    <OverlayTrigger
                      overlay={<Tooltip>{t(tk.common.openNewWindow)}</Tooltip>}
                    >
                      <Button
                        variant="outline-primary"
                        onClick={() =>
                          pok.attachments.saveFileInBrowser(attachment, 'open')
                        }
                      >
                        <FilePdf />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      overlay={<Tooltip>{t(tk.button.download)}</Tooltip>}
                    >
                      <Button
                        variant="outline-primary"
                        onClick={() =>
                          pok.attachments.saveFileInBrowser(
                            attachment,
                            'download',
                          )
                        }
                      >
                        <Download />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 bg-light pt-0">
        <Button variant="outline-secondary" onClick={props.onHide}>
          {t(tk.button.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
